import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setQuizInfo, setQuizResults } from '../../redux/slices/quizSlice';
import { updateUnlockedLevels, addAssessment as addAthleteAssessment } from '../../redux/slices/athleteSlice';
import { addAssessment as addCoachAssessment } from '../../redux/slices/coachSlice';
import { motion } from 'framer-motion';
import useGameIQQuestions from '../../hooks/quiz/useGameIQQuestions';
import useSubmitGameIQAttempt from '../../hooks/quiz/useSubmitGameIQAttempt';
import BackButton from '../../components/BackButton/BackButton';
import QuizQuestion from '../../components/QuizQuestion/QuizQuestion';
import QuestionNavigationPanel from '../../components/QuestionNavigationPanel/QuestionNavigationPanel';
import SportIcon from '../../components/SportIcon/SportIcon';
import styles from './QuizDashboard.module.css';

const QuizDashboard = () => {
  const { sport, currentLevel, questions, scorePercent, passed } = useSelector(state => state.quiz);
  const role = useSelector(state => state.auth.role);
  const dispatch = useDispatch();

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { fetchQuestions, questionsData } = useGameIQQuestions();
  const { submitQuiz } = useSubmitGameIQAttempt();

  useEffect(() => {
    if (sport && currentLevel && questions.length === 0) {
      fetchQuestions(sport, currentLevel);
    }
  }, [sport, currentLevel, questions.length, fetchQuestions]);

  useEffect(() => {
    if (questionsData?.getGameIQQuestions) {
      dispatch(setQuizInfo({
        sport,
        level: currentLevel,
        questions: questionsData.getGameIQQuestions
      }));
      setSelectedAnswers(Array(questionsData.getGameIQQuestions.length).fill(null));
    }
  }, [questionsData, dispatch, sport, currentLevel]);

  const handleAnswerSelect = (answer) => {
    const updated = [...selectedAnswers];
    updated[currentQuestionIndex] = answer;
    setSelectedAnswers(updated);
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    const formattedAnswers = selectedAnswers.map((answer, idx) => ({
      questionId: questions[idx]._id,
      selectedOption: answer
    }));

    try {
      const attempt = await submitQuiz({ sport, level: currentLevel, answers: formattedAnswers });

      dispatch(setQuizResults({
        scorePercent: attempt.scorePercent,
        passed: attempt.passed
      }));
      
      if (role === 'athlete') {
        dispatch(addAthleteAssessment(attempt));
    
        if (attempt.passed) {
          const nextLevel = currentLevel + 1;
          if (nextLevel <= 5) {
            dispatch(updateUnlockedLevels({ sport, level: nextLevel }));
          }
        }
      } else if (role === 'coach') {
        dispatch(addCoachAssessment(attempt));
      }

      setIsSubmitted(true);
    } catch (err) {
      console.error('❌ Submission failed:', err);
    }
  };

  const total = questions.length;
  const currentQuestion = questions[currentQuestionIndex];

  return (
    <div className={styles.dashboardWrapper}>
      <h1 className={styles.title}>
        <SportIcon sport={sport} />
        {sport.charAt(0).toUpperCase() + sport.slice(1)} Quiz - Level {currentLevel}
      </h1>
      <BackButton to="/dashboard" />
      <QuestionNavigationPanel
        questions={questions}
        selectedAnswers={selectedAnswers}
        currentIndex={currentQuestionIndex}
        onQuestionSelect={setCurrentQuestionIndex}
        isAnsweredFn={(q, i) => selectedAnswers[i] !== null}
      />
      {currentQuestion && (
        <QuizQuestion
          question={currentQuestion}
          currentIndex={currentQuestionIndex}
          totalQuestions={total}
          onAnswerSelect={handleAnswerSelect}
          selectedAnswer={selectedAnswers[currentQuestionIndex]}
          isSubmitted={isSubmitted}
        />
      )}
      <div className={styles.navigationButtons}>
        <button onClick={handlePrevious} disabled={currentQuestionIndex === 0}>Previous</button>
        {currentQuestionIndex < total - 1 && (
          <button onClick={handleNext}>Next</button>
        )}
        {currentQuestionIndex === total - 1 && !isSubmitted && (
          <button onClick={handleSubmit}>Submit Quiz</button>
        )}
      </div>
      {isSubmitted && (
  <motion.div
    className={styles.modalOverlay}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.3 }}
  >
    <motion.div
      className={styles.quizModalCard}
      initial={{ scale: 0.9, y: 20, opacity: 0 }}
      animate={{ scale: 1, y: 0, opacity: 1 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <h2 className={styles.resultsTitle}>Quiz Submitted!</h2>
      <p className={styles.traitScore}>Score: {scorePercent}%</p>
      <p className={styles.feedbackMessage}>
        Status: {passed ? 'Passed' : 'Failed'}
      </p>
      <BackButton to="/dashboard" />
    </motion.div>
  </motion.div>
)}

    </div>
  );
};

export default QuizDashboard;